import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

const TOKEN_KEY = "oracle_token";
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const setAuthToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearAuthToken = () => localStorage.removeItem(TOKEN_KEY);

client.interceptors.request.use((cfg) => {
  const t = getAuthToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && !String(err?.config?.url || "").includes("/auth/login")) {
      clearAuthToken();
      window.dispatchEvent(new Event("oracle-locked"));
    }
    return Promise.reject(err);
  }
);

export const authLogin = (password) => client.post("/auth/login", { password }).then((r) => r.data);

export const getResearchOverview = () => client.get("/research/overview").then(r => r.data);
export const getResearchProgram = (id) => client.get(`/research/programs/${id}`).then(r => r.data);
export const createResearchProgram = (body) => client.post("/research/programs", body, { timeout: 120000 }).then(r => r.data);
export const stepResearchProgram = (id) => client.post(`/research/programs/${id}/step`, {}, { timeout: 150000 }).then(r => r.data);
export const calibrateResearchJudge = (body) => client.post("/research/calibration", body, { timeout: 210000 }).then(r => r.data);
export const getResearchCalibrationJob = (id) => client.get(`/research/calibration/${id}`).then(r => r.data);
export const getResearchCampaign = () => client.get('/research/campaigns/current').then(r => r.data);
export const sealResearchCampaign = () => client.post('/research/campaigns/auto', {}, { timeout: 120000 }).then(r => r.data);
export const collectResearchCampaign = (id) => client.post(`/research/campaigns/${id}/collect`, {}, { timeout: 120000 }).then(r => r.data);
export const getMotherResearch = () => client.get('/research/mother/current').then(r => r.data);
export const registerMotherResearch = () => client.post('/research/mother/register', {}, { timeout: 120000 }).then(r => r.data);
export const screenMotherResearch = (id) => client.post(`/research/mother/${id}/screen`, {}, { timeout: 120000 }).then(r => r.data);
export const collectMotherResearch = (id) => client.post(`/research/mother/${id}/collect`, {}, { timeout: 120000 }).then(r => r.data);
export const getResearchBudget = (days=14) => client.get('/research/insights/budget', {params:{days}}).then(r => r.data);
export const getResearchFeasibility = () => client.get('/research/insights/feasibility').then(r => r.data);
export const getResearchSlippage = () => client.get('/research/insights/slippage').then(r => r.data);
export const getResearchFeed = () => client.get('/research/insights/feed').then(r => r.data);
export const getExecutionCapture = () => client.get('/research/insights/execution-capture').then(r => r.data);
export const getCreativeStrategist = () => client.get('/research/strategist').then(r=>r.data);
export const prepareCreativeBrief = () => client.post('/research/strategist/brief').then(r=>r.data);
export const runCreativeStrategist = () => client.post('/research/strategist/run').then(r=>r.data);
export const getCtMarketData = () => client.get('/ctrader/market-data').then(r => r.data);
export const setCtMarketData = (body) => client.put('/ctrader/market-data',body,{timeout:45000}).then(r => r.data);

export const getExecIntents = () => client.get("/exec/intents").then((r) => r.data);
export const reconcileExecIntents = () =>
  client.post("/exec/intents/reconcile-now", {}, { timeout: 60000 }).then((r) => r.data);
export const dismissExecIntent = (cid, note) =>
  client.post(`/exec/intents/${cid}/dismiss`, { note }).then((r) => r.data);

export const getParameters = () => client.get("/parameters").then((r) => r.data);
export const getEnv = () => client.get("/env").then((r) => r.data);
export const getExecRole = () => client.get("/ops/role").then((r) => r.data);
export const getOpsHealth = () => client.get("/ops/health").then((r) => r.data);
export const getListingRadar = () => client.get("/ops/listing-radar").then((r) => r.data);
export const armExecRole = () => client.post("/ops/role/arm").then((r) => r.data);
export const disarmExecRole = () => client.post("/ops/role/disarm").then((r) => r.data);
export const getPresets = () => client.get("/presets").then((r) => r.data);
export const generateCode = (config) =>
  client.post("/generate", { config }).then((r) => r.data);
export const validateConfig = (config) =>
  client.post("/validate", { config }).then((r) => r.data);
export const listConfigs = () => client.get("/configs").then((r) => r.data);
export const saveConfig = (name, config) =>
  client.post("/configs", { name, config }).then((r) => r.data);
export const deleteConfig = (id) =>
  client.delete(`/configs/${id}`).then((r) => r.data);

export const getMarketStatus = () => client.get("/market/status").then((r) => r.data);
export const runBacktest = (payload) =>
  client.post("/backtest", payload, { timeout: 180000 }).then((r) => r.data);

export const getNews = (refresh = false) =>
  client.get("/news", { params: { refresh }, timeout: 45000 }).then((r) => r.data);

// --- 5-instrument multi-strategy system (distinct from the Gold bot) ---
export const getMultiSymbols = () => client.get("/multi/symbols").then((r) => r.data);
export const getMultiCostModel = () => client.get("/multi/cost-model").then((r) => r.data);
export const runMultiBacktest = (payload) =>
  client.post("/multi/run-backtest", payload, { timeout: 180000 }).then((r) => r.data);
export const exportMultiTrades = (payload) =>
  client.post("/multi/export-trades", payload, { responseType: "blob", timeout: 180000 });

// --- walk-forward parameter optimisation + saved presets (Fase 9) ---
export const getOptimizeConfig = () => client.get("/multi/optimize-config").then((r) => r.data);
export const runOptimize = (payload) =>
  client.post("/multi/optimize", payload, { timeout: 240000 }).then((r) => r.data);
export const getMultiPresets = () => client.get("/multi/presets").then((r) => r.data);
export const saveMultiPreset = (payload) =>
  client.post("/multi/presets", payload).then((r) => r.data);
export const deleteMultiPreset = (strategy) =>
  client.delete(`/multi/presets/${strategy}`).then((r) => r.data);

// --- multi-instrument C# cBot generation ("Oracle Stock") ---
export const getMultiBotParams = () => client.get("/multi/bot-params").then((r) => r.data);
export const generateMultiBot = (config) =>
  client.post("/multi/generate", { config }).then((r) => r.data);
export const getPresetBotConfig = () => client.get("/multi/preset-bot-config").then((r) => r.data);

// --- Telegram daily briefing ---
export const getTelegramSettings = () => client.get("/telegram/settings").then((r) => r.data);
export const saveTelegramSettings = (payload) =>
  client.put("/telegram/settings", payload).then((r) => r.data);
export const testTelegram = (payload) =>
  client.post("/telegram/test", payload || {}).then((r) => r.data);
export const previewBriefing = () =>
  client.post("/telegram/preview", {}, { timeout: 120000 }).then((r) => r.data);
export const sendBriefingNow = () =>
  client.post("/telegram/send-now", {}, { timeout: 120000 }).then((r) => r.data);
export const detectTelegramChat = (botToken) =>
  client.get("/telegram/detect-chat", { params: botToken ? { botToken } : {} }).then((r) => r.data);

// --- Polymarket PAPER copy-trading (simulation only) ---
export const getPmOverview = () => client.get("/polymarket/overview").then((r) => r.data);
export const getPmSettings = () => client.get("/polymarket/settings").then((r) => r.data);
export const savePmSettings = (payload) =>
  client.put("/polymarket/settings", payload).then((r) => r.data);
export const resetPmAccount = (capital) =>
  client.post("/polymarket/account/reset", capital != null ? { capital } : {}).then((r) => r.data);
export const discoverPmTraders = (payload) =>
  client.post("/polymarket/discover", payload || {}, { timeout: 120000 }).then((r) => r.data);
export const listPmTraders = () => client.get("/polymarket/traders").then((r) => r.data);
export const trackPmTrader = (wallet, tracked) =>
  client.post("/polymarket/traders/track", { wallet, tracked }).then((r) => r.data);
export const addPmManualTrader = (wallet) =>
  client.post("/polymarket/traders/manual", { wallet }, { timeout: 60000 }).then((r) => r.data);
export const getPmPositions = () =>
  client.get("/polymarket/positions", { timeout: 60000 }).then((r) => r.data);
export const getPmTrades = (limit = 100) =>
  client.get("/polymarket/trades", { params: { limit } }).then((r) => r.data);
export const pmPollNow = () =>
  client.post("/polymarket/poll-now", {}, { timeout: 120000 }).then((r) => r.data);
export const simulatePm = (payload) =>
  client.post("/polymarket/simulate", payload || {}, { timeout: 120000 }).then((r) => r.data);
export const autoFollowPm = (payload) =>
  client.post("/polymarket/auto-follow", payload || {}, { timeout: 120000 }).then((r) => r.data);
export const measurePm = (payload) =>
  client.post("/polymarket/measure", payload || {}, { timeout: 150000 }).then((r) => r.data);

export const getOpportunities = () =>
  client.get("/polymarket/opportunities").then((r) => r.data);
export const scanOpportunities = () =>
  client.post("/polymarket/opportunities/scan", {}, { timeout: 90000 }).then((r) => r.data);
export const getCalibration = () =>
  client.get("/polymarket/calibration").then((r) => r.data);
export const refreshCalibration = (payload) =>
  client.post("/polymarket/calibration/refresh", payload || {}, { timeout: 30000 }).then((r) => r.data);
export const getSignalAccount = () =>
  client.get("/polymarket/signals/account").then((r) => r.data);
export const resetSignalAccount = (payload) =>
  client.post("/polymarket/signals/reset", payload || {}).then((r) => r.data);
export const runSignals = () =>
  client.post("/polymarket/signals/run-now", {}, { timeout: 90000 }).then((r) => r.data);
export const getEvBacktest = (params) =>
  client.get("/polymarket/signals/backtest", { params, timeout: 60000 }).then((r) => r.data);
export const refreshEvBacktest = () =>
  client.post("/polymarket/signals/backtest/refresh", {}, { timeout: 30000 }).then((r) => r.data);

// --- Whale Radar ---
export const getWhaleFills = (limit = 50, minUsd = 0) =>
  client.get("/polymarket/whales/fills", { params: { limit, minUsd } }).then((r) => r.data);
export const getWhaleAnalytics = (limit = 30) =>
  client.get("/polymarket/whales/analytics", { params: { limit } }).then((r) => r.data);
export const getWhaleBacktest = (wallet) =>
  client.get(`/polymarket/whales/backtest/${wallet}`, { timeout: 60000 }).then((r) => r.data);
export const scanWhalesNow = () =>
  client.post("/polymarket/whales/scan-now", {}, { timeout: 60000 }).then((r) => r.data);
export const getWhaleStreamStatus = () =>
  client.get("/polymarket/whales/stream-status").then((r) => r.data);
export const getWhaleCopies = (limit = 100) =>
  client.get("/polymarket/whales/copies", { params: { limit } }).then((r) => r.data);
export const closeWhaleCopy = (id) =>
  client.post(`/polymarket/whales/copies/${id}/close`, {}).then((r) => r.data);
export const cancelAllWhaleCopies = () =>
  client.post("/polymarket/whales/copies/cancel-all", {}).then((r) => r.data);
export const reconcilePmCopies = () =>
  client.post("/polymarket/whales/copies/reconcile-now", {}, { timeout: 60000 })
    .then((r) => r.data);

// --- Real execution (CLOB) controls — dormant until armed ---
export const getExecStatus = () =>
  client.get("/polymarket/whales/execution/status").then((r) => r.data);
export const execPreflight = () =>
  client.post("/polymarket/whales/execution/preflight", {}, { timeout: 30000 }).then((r) => r.data);
export const execTestRealBtc5m = () =>
  client.post("/polymarket/whales/execution/test-real-btc5m", {}, { timeout: 45000 }).then((r) => r.data);
export const execProbeReport = () =>
  client.get("/polymarket/whales/execution/probe-report").then((r) => r.data);
export const getPmRealOverview = () =>
  client.get("/polymarket/real/overview", { timeout: 30000 }).then((r) => r.data);
export const setKillSwitch = (enabled) =>
  client.post("/polymarket/whales/execution/kill-switch", { enabled }).then((r) => r.data);
export const armRealExecution = (enabled) =>
  client.post("/polymarket/whales/execution/arm", { enabled }).then((r) => r.data);

// --- +EV signals real execution (CLOB) controls — dormant until armed ---
export const getSignalsExecStatus = () =>
  client.get("/polymarket/signals/execution/status").then((r) => r.data);
export const setSignalsKillSwitch = (enabled) =>
  client.post("/polymarket/signals/execution/kill-switch", { enabled }).then((r) => r.data);
export const armSignalsExecution = (enabled) =>
  client.post("/polymarket/signals/execution/arm", { enabled }).then((r) => r.data);

// --- Hyperliquid auto-trading bot (strategy + leaderboard copy; PAPER) ---
export const getHlCarry = () => client.get("/hyperliquid/carry").then((r) => r.data);
export const runHlCarry = () =>
  client.post("/hyperliquid/carry/run", {}, { timeout: 60000 }).then((r) => r.data);
export const saveHlCarrySettings = (payload) =>
  client.put("/hyperliquid/carry/settings", payload).then((r) => r.data);
export const getHlOverview = () => client.get("/hyperliquid/overview").then((r) => r.data);
export const getHlSettings = () => client.get("/hyperliquid/settings").then((r) => r.data);
export const saveHlSettings = (payload) =>
  client.put("/hyperliquid/settings", payload).then((r) => r.data);
export const applyHlBasePreset = () =>
  client.post("/hyperliquid/settings/base-preset").then((r) => r.data);
export const getHlAccount = () => client.get("/hyperliquid/account").then((r) => r.data);
export const getHlAccountReal = () => client.get("/hyperliquid/account/real").then((r) => r.data);
export const getHlReconcile = () => client.get("/hyperliquid/reconcile").then((r) => r.data);
export const getHlGraduation = () => client.get("/hyperliquid/graduation").then((r) => r.data);
export const getHlCopyLatency = () => client.get("/hyperliquid/latency").then((r) => r.data);
export const getHlCopyAudit = () => client.get("/hyperliquid/copy/audit").then((r) => r.data);
export const getHlCopyToll = (refresh = false) =>
  client.get("/hyperliquid/copy/toll", { params: { refresh }, timeout: 120000 }).then((r) => r.data);
export const getHlHoldExperiment = (days = 60) =>
  client.get("/hyperliquid/copy/hold-experiment", { params: { days } }).then((r) => r.data);
export const getBrokerBakeoff = () =>
  client.get("/ctrader/broker-bakeoff", { timeout: 60000 }).then((r) => r.data);
export const getTickProbe = () => client.get("/intraday/tick-probe").then((r) => r.data);
export const runTickProbe = () =>
  client.post("/intraday/tick-probe/run-now", {}, { timeout: 120000 }).then((r) => r.data);
export const getPortfolioEquity = (days = 180) => client.get(`/portfolio/equity?days=${days}`).then((r) => r.data);
export const snapshotPortfolioEquity = () => client.post("/portfolio/equity/snapshot").then((r) => r.data);
export const hlGraduationEvaluate = () => client.post("/hyperliquid/graduation/evaluate").then((r) => r.data);
export const hlCloseOrphans = () =>
  client.post("/hyperliquid/reconcile/close-orphans", {}, { timeout: 60000 }).then((r) => r.data);
export const getHlJournal = (days = 30) => client.get(`/hyperliquid/journal?days=${days}`).then((r) => r.data);
export const getHlCopyConviction = (days = 7) => client.get(`/hyperliquid/copy/conviction?days=${days}`).then((r) => r.data);
export const getHlAdaptive = () => client.get("/hyperliquid/adaptive").then((r) => r.data);
export const resetHlAccount = (capital) =>
  client.post("/hyperliquid/account/reset", capital != null ? { capital } : {}).then((r) => r.data);
export const getHlSignals = () =>
  client.get("/hyperliquid/signals", { timeout: 90000 }).then((r) => r.data);
export const hlRunNow = () =>
  client.post("/hyperliquid/run-now", {}, { timeout: 120000 }).then((r) => r.data);
export const getHlMarkets = (limit = 60) =>
  client.get("/hyperliquid/markets", { params: { limit }, timeout: 30000 }).then((r) => r.data);
export const runHlBacktest = (payload) =>
  client.post("/hyperliquid/backtest", payload || {}, { timeout: 150000 }).then((r) => r.data);
export const runHlSweep = (payload) =>
  client.post("/hyperliquid/backtest/sweep", payload || {}, { timeout: 180000 }).then((r) => r.data);
export const runHlWalkForward = (payload) =>
  client.post("/hyperliquid/backtest/walkforward", payload || {}, { timeout: 240000 }).then((r) => r.data);

// Hyperliquid Brain (Cervello) — learns from every closed trade
export const getHlBrainOverview = (days = 30, mode = "all") =>
  client.get("/hyperliquid/brain/overview", { params: { days, mode }, timeout: 60000 }).then((r) => r.data);
export const getHlBrainDiary = (days = 14, mode = "all", force = false) =>
  client.get("/hyperliquid/brain/diary", { params: { days, mode, force }, timeout: 90000 }).then((r) => r.data);
export const getHlBrainConfidence = () =>
  client.get("/hyperliquid/brain/confidence", { timeout: 40000 }).then((r) => r.data);

// Central multi-platform Brain (read-only): hyperliquid | ctrader | polymarket
export const getBrainOverview = (platform = "hyperliquid", days = 30, mode = "all") =>
  client.get("/brain/overview", { params: { platform, days, mode }, timeout: 60000 }).then((r) => r.data);
export const getBrainDiary = (platform = "hyperliquid", days = 14, mode = "all", force = false) =>
  client.get("/brain/diary", { params: { platform, days, mode, force }, timeout: 90000 }).then((r) => r.data);
export const getBrainDiaryUnified = (days = 14, force = false) =>
  client.get("/brain/diary-unified", { params: { days, force }, timeout: 120000 }).then((r) => r.data);
export const getTunerStatus = () => client.get("/brain/tuner").then((r) => r.data);
export const getBrainReminders = () => client.get("/brain/reminders").then((r) => r.data);
export const addBrainReminder = (message, dueAt) =>
  client.post("/brain/reminders", { message, dueAt }).then((r) => r.data);
export const deleteBrainReminder = (id) =>
  client.delete(`/brain/reminders/${id}`).then((r) => r.data);
export const decideTunerProposal = (id, approve) =>
  client.post(`/brain/tuner/${id}/${approve ? "approve" : "reject"}`).then((r) => r.data);
export const rollbackTunerProposal = (id) =>
  client.post(`/brain/tuner/${id}/rollback`).then((r) => r.data);
export const getSelfScoring = () => client.get("/brain/self-scoring").then((r) => r.data);

// Hyperliquid copy-trading
export const getHlCopyAccount = () => client.get("/hyperliquid/copy/account").then((r) => r.data);
export const resetHlCopyAccount = (capital) =>
  client.post("/hyperliquid/copy/account/reset", capital != null ? { capital } : {}).then((r) => r.data);
export const getHlTraders = () => client.get("/hyperliquid/copy/traders").then((r) => r.data);
export const discoverHlTraders = () =>
  client.post("/hyperliquid/copy/discover", {}, { timeout: 60000 }).then((r) => r.data);
export const trackHlTrader = (wallet, tracked) =>
  client.post("/hyperliquid/copy/traders/track", { wallet, tracked }).then((r) => r.data);
export const setHlPaperOnly = (wallet, paperOnly) =>
  client.post("/hyperliquid/copy/traders/paper-only", { wallet, paperOnly }).then((r) => r.data);
export const getHlRosterEvents = (wallet) =>
  client.get("/hyperliquid/copy/roster-events", { params: wallet ? { wallet } : {} }).then((r) => r.data);
export const addHlManualTrader = (wallet) =>
  client.post("/hyperliquid/copy/traders/manual", { wallet }, { timeout: 30000 }).then((r) => r.data);
export const hlCopyRunNow = () =>
  client.post("/hyperliquid/copy/run-now", {}, { timeout: 120000 }).then((r) => r.data);
export const getHlMirrorOrders = () =>
  client.get("/hyperliquid/copy/mirror-orders").then((r) => r.data);
export const getHlTraderProfile = (wallet, refresh = false) =>
  client.get(`/hyperliquid/copy/traders/${wallet}/profile`, { params: { refresh }, timeout: 90000 }).then((r) => r.data);
export const getHlTraderOrders = (wallet) =>
  client.get(`/hyperliquid/copy/traders/${wallet}/orders`).then((r) => r.data);
export const getHlTraderSynthesis = (wallet) =>
  client.get(`/hyperliquid/copy/traders/${wallet}/synthesize`, { timeout: 90000 }).then((r) => r.data);
export const generateHlTraderSummary = (wallet, refresh = false) =>
  client.post(`/hyperliquid/copy/traders/${wallet}/summary`, {}, { params: { refresh }, timeout: 60000 }).then((r) => r.data);

// Hyperliquid real execution controls — dormant until armed + wallet
export const getHlExecStatus = () =>
  client.get("/hyperliquid/execution/status").then((r) => r.data);
export const hlExecPreflight = () =>
  client.post("/hyperliquid/execution/preflight", {}, { timeout: 30000 }).then((r) => r.data);
export const setHlKillSwitch = (enabled) =>
  client.post("/hyperliquid/execution/kill-switch", { enabled }).then((r) => r.data);
export const armHlExecution = (enabled) =>
  client.post("/hyperliquid/execution/arm", { enabled }).then((r) => r.data);

// --- cTrader Open API + Gold (XAU) paper engine ---
export const getCtStatus = () =>
  client.get("/ctrader/status", { timeout: 25000 }).then((r) => r.data);
export const getCtSettings = () => client.get("/ctrader/settings").then((r) => r.data);
export const ctAuthLoginUrl = () => {
  const origin = window.location.origin;
  return `${origin}/api/ctrader/auth/login?redirect_uri=${encodeURIComponent(`${origin}/api/ctrader/auth/callback`)}`;
};

export const getGoldStatus = () => client.get("/ctrader/gold/status").then((r) => r.data);
export const getGoldSettings = () => client.get("/ctrader/gold/settings").then((r) => r.data);
export const saveGoldSettings = (payload) =>
  client.put("/ctrader/gold/settings", payload).then((r) => r.data);
export const goldRunNow = () =>
  client.post("/ctrader/gold/run-now", {}, { timeout: 90000 }).then((r) => r.data);

// --- cTrader live execution (real orders) ---
export const getCtAccounts = () =>
  client.get("/ctrader/accounts", { timeout: 30000 }).then((r) => r.data);
export const saveCtSettings = (payload) =>
  client.put("/ctrader/settings", payload).then((r) => r.data);
export const getCtAccount = (accountId) =>
  client.get("/ctrader/account", { params: { account_id: accountId }, timeout: 30000 }).then((r) => r.data);
export const getCtUnrealizedPnl = (accountId) =>
  client.get("/ctrader/unrealized-pnl", { params: { account_id: accountId }, timeout: 30000 }).then((r) => r.data);
export const placeCtOrder = (payload) =>
  client.post("/ctrader/order", payload, { timeout: 30000 }).then((r) => r.data);
export const closeCtPosition = (payload) =>
  client.post("/ctrader/close", payload, { timeout: 30000 }).then((r) => r.data);
export const autoExecRunNow = () =>
  client.post("/ctrader/auto-exec/run-now", {}, { timeout: 90000 }).then((r) => r.data);
export const getCopyCrowding = () => client.get("/hyperliquid/copy/crowding").then((r) => r.data);
export const runCopyCrowding = () => client.post("/hyperliquid/copy/crowding/run", {}, { timeout: 60000 }).then((r) => r.data);
export const getCtPreflight = (accountId) =>
  client.get(`/ctrader/preflight/${accountId}`, { timeout: 60000 }).then((r) => r.data);
export const syncCtRealTrades = (days) =>
  client.post("/ctrader/trades/sync-real", {}, { params: days ? { days } : {}, timeout: 120000 }).then((r) => r.data);
export const getCtRealTrades = (limit = 50) =>
  client.get("/ctrader/trades/real", { params: { limit } }).then((r) => r.data);
export const getGoldCompare = (days = 30) =>
  client.get("/ctrader/gold/compare", { params: { days } }).then((r) => r.data);
export const getAdvisor = (force = false) =>
  client.get("/brain/advisor", { params: force ? { force: true } : {}, timeout: 120000 }).then((r) => r.data);
export const sendAdvisorFeedback = (adviceId, action) =>
  client.post("/brain/advisor/feedback", { adviceId, action }).then((r) => r.data);
export const getPmOracleStatus = () => client.get("/polymarket/oracle/status").then((r) => r.data);
export const getPmScoutStatus = () => client.get("/polymarket/scout").then((r) => r.data);
export const pmScoutRun = () =>
  client.post("/polymarket/scout/run", {}, { timeout: 180000 }).then((r) => r.data);
export const getFavFadeStatus = () => client.get("/polymarket/oracle/favfade/status").then((r) => r.data);
export const favFadeTick = () =>
  client.post("/polymarket/oracle/favfade/tick", {}, { timeout: 120000 }).then((r) => r.data);
export const pmOracleRunNow = () =>
  client.post("/polymarket/oracle/run-now", {}, { timeout: 180000 }).then((r) => r.data);
export const getIntradayStatus = () => client.get("/intraday/status").then((r) => r.data);
export const saveIntradaySettings = (payload) =>
  client.put("/intraday/settings", payload).then((r) => r.data);
export const intradayRunNow = () =>
  client.post("/intraday/run-now", {}, { timeout: 60000 }).then((r) => r.data);

// Cross-section combined paper portfolio
export const getPortfolioSummary = () => client.get("/portfolio/summary").then((r) => r.data);
export const saveCapitalBaseline = (payload) => client.put("/portfolio/baseline", payload).then((r) => r.data);

// Monitoring / A-B (config versioning + closed-trade log)
export const getAbReport = (platform = "hyperliquid") =>
  client.get(`/monitoring/ab`, { params: { platform } }).then((r) => r.data);
export const getTradeLog = (platform = "hyperliquid", limit = 100) =>
  client.get(`/monitoring/trades`, { params: { platform, limit } }).then((r) => r.data);
export const getCopyStyleReport = (platform = "hyperliquid") =>
  client.get(`/monitoring/copy-style`, { params: { platform } }).then((r) => r.data);
export const getCopyTraderReport = (platform = "hyperliquid") =>
  client.get(`/monitoring/copy-traders`, { params: { platform } }).then((r) => r.data);

export const getCouncil = () => client.get("/council/latest").then((r) => r.data);
export const runCouncil = () => client.post("/council/run").then((r) => r.data);
export const getTrendRadar = () => client.get("/trend-radar").then((r) => r.data);
export const runTrendRadarScan = () => client.post("/trend-radar/scan", {}, { timeout: 90000 }).then((r) => r.data);
export const validateTrendRadar = (symbol) =>
  client.post("/trend-radar/validate", { symbol }, { timeout: 120000 }).then((r) => r.data);
export const getTrendRadarValidations = () => client.get("/trend-radar/validations").then((r) => r.data);
export const getRotationStatus = () => client.get("/rotation/status").then((r) => r.data);
export const getRiskStatus = () => client.get("/risk/status").then((r) => r.data);
export const updateRiskSettings = (patch) => client.post("/risk/settings", patch).then((r) => r.data);
export const getTcaStats = () => client.get("/tca/stats").then((r) => r.data);
export const getGexLatest = (symbol = "NDX") => client.get(`/gex/latest?symbol=${symbol}`).then((r) => r.data);
export const getVolReport = () => client.get("/vol/report", { timeout: 60000 }).then((r) => r.data);
export const runVolCollect = () =>
  client.post("/vol/collect", {}, { timeout: 120000 }).then((r) => r.data);
export const syncVolHistory = () =>
  client.post("/vol/history-sync", {}, { timeout: 180000 }).then((r) => r.data);
export const getVolHedge = () =>
  client.get("/vol/hedge", { timeout: 120000 }).then((r) => r.data);
export const runGexSnapshot = () => client.post("/gex/snapshot", {}, { timeout: 60000 }).then((r) => r.data);
export const getFtmoSolver = () => client.get("/ftmo/solver").then((r) => r.data);
export const runFtmoSolver = () => client.post("/ftmo/solver/run", {}, { timeout: 30000 }).then((r) => r.data);
export const getFtmoChallenge = () => client.get("/ftmo/challenge").then((r) => r.data);
export const startFtmoChallenge = (mode) => client.post("/ftmo/challenge/start", { mode }).then((r) => r.data);
export const cycleFtmoChallenge = () => client.post("/ftmo/challenge/cycle", {}, { timeout: 120000 }).then((r) => r.data);
export const getFtmoFirms = () => client.get("/ftmo/firms").then((r) => r.data);
export const getFtmoParallel = () => client.get("/ftmo/parallel").then((r) => r.data);
export const getFtmoGoGate = () => client.get("/ftmo/go-gate", { timeout: 30000 }).then((r) => r.data);
export const saveFtmoGoGate = (payload) => client.put("/ftmo/go-gate", payload, { timeout: 30000 }).then((r) => r.data);
export const checkFtmoGoGate = () => client.post("/ftmo/go-gate/check", {}, { timeout: 30000 }).then((r) => r.data);
export const runFtmoParallel = (firmB) => client.post("/ftmo/parallel/run", { firmB }).then((r) => r.data);
export const getFtmoSprint = () => client.get("/ftmo/sprint").then((r) => r.data);
export const getFtmoProgram = () => client.get("/ftmo/program").then((r) => r.data);
export const getFoundry = () => client.get("/foundry").then((r) => r.data);
export const foundryRun = (family) => client.post(`/foundry/run/${family}`).then((r) => r.data);
export const getCoint = () => client.get("/coint").then((r) => r.data);
export const cointRun = () => client.post("/coint/run").then((r) => r.data);
export const getNotifyPrefs = () => client.get("/telegram/notify-prefs").then((r) => r.data);
export const setNotifyPref = (category, level) => client.post("/telegram/notify-prefs", { category, level }).then((r) => r.data);
export const flushDigest = () => client.post("/telegram/digest/flush").then((r) => r.data);
export const getFtmoMethod = () => client.get("/ftmo/method").then((r) => r.data);
export const getFtmoReadiness = () => client.get("/ftmo/readiness").then((r) => r.data);
export const getWarehouse = () => client.get("/warehouse").then((r) => r.data);
export const getWarehouseIntegrity = (symbol, tf) =>
  client.get("/warehouse/integrity", { params: { symbol, tf }, timeout: 60000 }).then((r) => r.data);
export const resetFtmoSprint = () => client.post("/ftmo/sprint/reset").then((r) => r.data);
export const getE8Sprint = () => client.get("/e8/sprint").then((r) => r.data);
export const resetE8Sprint = () => client.post("/e8/sprint/reset").then((r) => r.data);
export const getFomcSniper = () => client.get("/fomc-sniper").then((r) => r.data);
export const runFomcDryRun = () => client.post("/fomc-sniper/dry-run").then((r) => r.data);

export const getSignalCopier = () => client.get("/signal-copier/status").then((r) => r.data);
export const signalCopierLoginStart = (phone, force = false) => client.post("/signal-copier/login/start", { phone, force }).then((r) => r.data);
export const signalCopierLoginCode = (code) => client.post("/signal-copier/login/code", { code }).then((r) => r.data);
export const signalCopierLoginPassword = (password) => client.post("/signal-copier/login/password", { password }).then((r) => r.data);
export const signalCopierLogout = () => client.post("/signal-copier/logout").then((r) => r.data);
export const signalCopierSettings = (payload) => client.post("/signal-copier/settings", payload).then((r) => r.data);
export const signalCopierChannel = (payload) => client.post("/signal-copier/channel", payload).then((r) => r.data);
export const signalCopierResolve = () => client.post("/signal-copier/resolve-channel").then((r) => r.data);
export const getSignalCopierMessages = (limit = 50) => client.get(`/signal-copier/messages?limit=${limit}`).then((r) => r.data);
export const getSignalCopierMarkout = () => client.get("/signal-copier/markout").then((r) => r.data);

export const getXWatch = () => client.get("/x/status").then((r) => r.data);
export const xWatchSettings = (payload) => client.put("/x/settings", payload).then((r) => r.data);
export const xWatchCredentials = (payload) => client.post("/x/credentials", payload).then((r) => r.data);
export const xWatchClearCredentials = () => client.delete("/x/credentials").then((r) => r.data);
export const xWatchPollNow = () => client.post("/x/poll-now", {}, { timeout: 90000 }).then((r) => r.data);
export const getXWatchPosts = (limit = 20) => client.get(`/x/posts?limit=${limit}`).then((r) => r.data);
export const xWatchTestSend = () => client.post("/x/test-send", {}, { timeout: 30000 }).then((r) => r.data);
export const getXClientScript = (kind = "relay") => client.get(`/x/client-script?kind=${kind}`).then((r) => r.data);

export const getInstaArchive = () => client.get("/insta-archive/status").then((r) => r.data);
export const setInstaArchiveConfig = (payload) => client.post("/insta-archive/config", payload).then((r) => r.data);
export const runInstaArchive = (limit = 50) => client.post("/insta-archive/run", { limit }).then((r) => r.data);
export const searchInstaArchive = (q, limit = 20) => client.get(`/insta-archive/search?q=${encodeURIComponent(q)}&limit=${limit}`).then((r) => r.data);
export const instaArchiveLogin = (payload) => client.post("/insta-archive/login", payload).then((r) => r.data);
export const instaArchiveChallenge = (code) => client.post("/insta-archive/challenge", { code }).then((r) => r.data);
export const getValidationGate = () => client.get("/validation-gate").then((r) => r.data);
export const getFactory = () => client.get("/factory").then((r) => r.data);
export const runFactory = (maxRepos = 10) => client.post("/factory/run", { maxRepos }).then((r) => r.data);
export const setFactoryLoop = (cfg) => client.post("/factory/loop", cfg).then((r) => r.data);
export const getFactoryLive = () => client.get("/factory/live").then((r) => r.data);
export const getMnqPaper = () => client.get("/mnq/paper").then((r) => r.data);
export const getStrategyVizList = () => client.get("/strategy/viz/list").then((r) => r.data);
export const getStrategyViz = (key) => client.get("/strategy/viz", { params: { key } }).then((r) => r.data);
export const setMnqPaperConfig = (payload) => client.post("/mnq/paper/config", payload).then((r) => r.data);
export const getPortfolioStress = () => client.get("/portfolio/stress").then((r) => r.data);
export const setFactoryLiveConfig = (cfg) => client.post("/factory/live/config", cfg).then((r) => r.data);
export const getTraderDna = () => client.get("/trader-dna").then((r) => r.data);
export const runTraderDna = () => client.post("/trader-dna/run").then((r) => r.data);
export const distillTraderDna = (addr) => client.post(`/trader-dna/distill/${addr}`).then((r) => r.data);
export const getDnaDistillery = () => client.get("/trader-dna/distillery").then((r) => r.data);
export const getBtcSpecialists = () => client.get("/btc-specialists").then((r) => r.data);
export const scanBtcSpecialists = () => client.post("/btc-specialists/scan").then((r) => r.data);
export const runFtmoFirms = () => client.post("/ftmo/firms/run", {}, { timeout: 30000 }).then((r) => r.data);
export const getFtmoPortfolio = (budget, firmId) =>
  client.get("/ftmo/portfolio", { params: { budget, ...(firmId ? { firmId } : {}) } }).then((r) => r.data);
export const getGoalSimSources = () => client.get("/goal-sim/sources").then((r) => r.data);
export const runGoalSim = (source, targetUsd, maxDdUsd) =>
  client.post("/goal-sim/run", { source, targetUsd, maxDdUsd }, { timeout: 60000 }).then((r) => r.data);
export const getGlobalKillSwitch = () => client.get("/kill-switch").then((r) => r.data);
export const setGlobalKillSwitch = (active) => client.post("/kill-switch", { active }).then((r) => r.data);
export const getNewsAnalyst = () => client.get("/news/analyst").then((r) => r.data);
export const refreshNewsAnalyst = () => client.post("/news/analyst/refresh").then((r) => r.data);
export const saveNewsAnalystSettings = (patch) =>
  client.put("/news/analyst/settings", patch).then((r) => r.data);
export const getStrategyLab = () => client.get("/strategy-lab/status").then((r) => r.data);
export const runStrategyLab = () => client.post("/strategy-lab/run-now").then((r) => r.data);
export const getStrategyVerdicts = () => client.get("/strategy-verdicts").then((r) => r.data);
export const getHypoPortfolio = () => client.get("/hypo-portfolio/status").then((r) => r.data);
export const jarvisChat = (sessionId, message) =>
  client.post("/jarvis/chat", { sessionId, message }, { timeout: 90000 }).then((r) => r.data);
export const jarvisHistory = (sessionId) =>
  client.get(`/jarvis/history/${sessionId}`).then((r) => r.data);
export const jarvisMemory = () => client.get("/jarvis/memory").then((r) => r.data);
export const jarvisMemoryDelete = (id) => client.delete(`/jarvis/memory/${id}`).then((r) => r.data);
export const jarvisTts = (text) =>
  client.post("/jarvis/tts", { text }, { responseType: "blob", timeout: 60000 }).then((r) => r.data);
export const jarvisStt = (blob, filename) => {
  const fd = new FormData();
  fd.append("file", blob, filename);
  return client.post("/jarvis/stt", fd, { timeout: 60000 }).then((r) => r.data);
};

export const getBrainBrief = (force = false) =>
  client.get("/brain/brief", { params: force ? { force: true } : {} }).then((r) => r.data);

export const getHubPulse = (days = 30) =>
  client.get("/hub/pulse", { params: { days } }).then((r) => r.data);

export const getFamilyRegistry = () => client.get("/family-registry").then((r) => r.data);
export const runFamilyRegistry = () => client.post("/family-registry/run", {}).then((r) => r.data);
export const getTcaCurves = () => client.get("/tca/curves").then((r) => r.data);
export const getSlippageTruth = () => client.get("/tca/slippage-truth").then((r) => r.data);
export const getBookProbe = () => client.get("/tca/book-probe").then((r) => r.data);
export const getTcaReport = (days = 90) => client.get(`/tca/report?days=${days}`).then((r) => r.data);

export const getBackupStatus = () => client.get("/system/backup-status").then((r) => r.data);
export const getFactoryTruth = () => client.get("/factory/truth").then((r) => r.data);
export const getReplication = () => client.get("/factory/replication").then((r) => r.data);
export const runReplication = (limit = 3) =>
  client.post("/factory/replication/run", null, { params: { limit } }).then((r) => r.data);
export const regradeReplication = (limit = 4) =>
  client.post("/factory/replication/regrade", null, { params: { limit } }).then((r) => r.data);
export const getPrereg = () => client.get("/factory/prereg", { timeout: 60000 }).then((r) => r.data);
export const getPreregForward = () => client.get("/factory/prereg/forward").then((r) => r.data);
export const stepPreregForward = (hid) =>
  client.post("/factory/prereg/forward/step", null,
    { params: hid ? { hid } : {}, timeout: 300000 }).then((r) => r.data);
export const getRevalidation = () =>
  client.get("/factory/revalidation", { timeout: 60000 }).then((r) => r.data);
export const getMuseum = (q) =>
  client.get("/factory/museum", { params: q ? { q } : {} }).then((r) => r.data);
export const getBottleneck = (force = false) =>
  client.get("/factory/bottleneck", { params: { force }, timeout: 120000 }).then((r) => r.data);
export const runRevalidation = (n = 0) =>
  client.post("/factory/revalidation/run", null, { params: { n }, timeout: 60000 }).then((r) => r.data);
export const stopRevalidation = () =>
  client.post("/factory/revalidation/stop").then((r) => r.data);
export const runPrereg = (hid) =>
  client.post(`/factory/prereg/run/${hid}`, null, { timeout: 30000 }).then((r) => r.data);
export const getInvestOverview = () => client.get("/invest/overview").then((r) => r.data);
export const investRebalanceNow = () => client.post("/invest/rebalance-now").then((r) => r.data);
export const runInvestBacktests = () => client.post("/invest/backtests/run").then((r) => r.data);
export const getDiscovery = () => client.get("/invest/discovery").then((r) => r.data);
export const runDiscoveryScreen = () => client.post("/invest/discovery/run").then((r) => r.data);
export const getFundOverview = () => client.get("/fund/overview").then((r) => r.data);
export const getPuppeteerStatus = () => client.get("/puppeteer/status").then((r) => r.data);
export const getVerdicts = () => client.get("/puppeteer/verdicts").then((r) => r.data);
export const investigateVerdict = (id, force = false) =>
  client.get("/puppeteer/verdicts/investigate", { params: { id, force }, timeout: 180000 })
    .then((r) => r.data);
export const getRegimeMcSources = () => client.get("/regime-mc/sources").then((r) => r.data);
export const getRegimeMc = (params) =>
  client.get("/regime-mc", { params, timeout: 120000 }).then((r) => r.data);
export const getPropSimOptions = () => client.get("/ftmo/prop-sim/options").then((r) => r.data);
export const getBookMr = (params) =>
  client.get("/hyperliquid/orderflow/mean-reversion", { params, timeout: 180000 })
    .then((r) => r.data);
export const getPropSim = (params) =>
  client.get("/ftmo/prop-sim", { params, timeout: 120000 }).then((r) => r.data);
export const runPuppeteerReport = () => client.post("/puppeteer/report/run").then((r) => r.data);
export const decidePuppeteerProposal = (id, approve) =>
  client.post(`/puppeteer/proposals/${id}/decide`, { approve }).then((r) => r.data);
export const getFundLadder = () => client.get("/fund/ladder").then((r) => r.data);
export const putFundSettings = (payload) => client.put("/fund/settings", payload).then((r) => r.data);
export const applyFundStep = (payload) => client.post("/fund/ladder/apply", payload).then((r) => r.data);
export const getDistillerReport = () => client.get("/factory/distill").then((r) => r.data);
export const runDistiller = () => client.post("/factory/distill/run").then((r) => r.data);
export const exportSystemState = () => client.post("/system/export-state", {}, { timeout: 120000 }).then((r) => r.data);
export const restoreSystemState = () => client.post("/system/restore-state", {}, { timeout: 120000 }).then((r) => r.data);

export const getWarehouseRebuild = () => client.get("/warehouse/rebuild").then((r) => r.data);
export const getWarehouseFreshness = () => client.get("/warehouse/freshness").then((r) => r.data);
export const getFeedWatch = () => client.get("/warehouse/feed-watch").then((r) => r.data);
export const syncWarehouseNow = (all = false) =>
  client.post("/warehouse/sync-now", { all }, { timeout: 180000 }).then((r) => r.data);
export const startWarehouseRebuild = () => client.post("/warehouse/rebuild", {}).then((r) => r.data);

export const getRegimeCurrent = () => client.get("/regime/current").then((r) => r.data);
export const getRegimeAutopsy = () => client.get("/regime/autopsy").then((r) => r.data);
export const runRegimeAutopsy = () => client.post("/regime/autopsy/run", {}).then((r) => r.data);

export const getRegimeEnsemble = () => client.get("/regime/ensemble").then((r) => r.data);
export const runRegimeEnsemble = () => client.post("/regime/ensemble/run", {}).then((r) => r.data);

export const getHypotheses = () => client.get("/hypotheses").then((r) => r.data);

export const getPortfolioBook = () => client.get("/portfolio/book").then((r) => r.data);
export const buildPortfolioBook = () => client.post("/portfolio/build", {}).then((r) => r.data);

export const getGateCalibration = () => client.get("/gate/calibration").then((r) => r.data);
export const runGateCalibration = () => client.post("/gate/calibration/run", {}, { timeout: 120000 }).then((r) => r.data);

export const getWatchdog = () => client.get("/watchdog").then((r) => r.data);
export const getLoopHealth = () => client.get("/system/loop-health").then((r) => r.data);
export const getSignatureGuard = () => client.get("/system/signature-guard").then((r) => r.data);
export const checkSignatureGuard = () =>
  client.post("/system/signature-guard/check", null, { timeout: 180000 }).then((r) => r.data);
export const checkWatchdog = () => client.post("/watchdog/check", {}).then((r) => r.data);

export const getResearchPrior = () => client.get("/research/prior").then((r) => r.data);
export const getYoutubeScanner = () => client.get("/factory/youtube").then((r) => r.data);
export const getRankedAa = () => client.get("/research/rankedaa").then((r) => r.data);
export const runRankedAa = () => client.post("/research/rankedaa/run").then((r) => r.data);
export const getSynapse = () => client.get("/synapse").then((r) => r.data);
export const runSynapse = () => client.post("/synapse/run").then((r) => r.data);
export const reconcileTrades = () => client.get("/trades/reconcile").then((r) => r.data);
export const applyProvenAttribution = () => client.post("/trades/reconcile/apply").then((r) => r.data);
export const attributeTradesBulk = (ids, father, note) =>
  client.post("/trades/attribute-bulk", { ids, father, note }).then((r) => r.data);
export const relabelCtrader = (days = 120) =>
  client.post("/trades/relabel-ctrader", null, { params: { days } }).then((r) => r.data);
export const getExperiments = () => client.get("/experiments").then((r) => r.data);
export const getRealLedger = () => client.get("/ledger/real").then((r) => r.data);
export const getFatherVerdict = (minN) =>
  client.get("/ledger/verdict", { params: minN ? { minN } : {} }).then((r) => r.data);
export const attributeTrade = (id, father, note) =>
  client.post(`/trades/${id}/attribute`, { father, note }).then((r) => r.data);
export const getPortfolioRisk = (force = false) =>
  client.get("/risk/portfolio", { params: force ? { force: true } : {} }).then((r) => r.data);
export const getJournalQuant = (platform, mode) =>
  client.get("/journal/quant", { params: { ...(platform && { platform }), ...(mode && { mode }) } }).then((r) => r.data);
export const setGlobalRiskCap = (cfg) => client.post("/risk/global/config", cfg).then((r) => r.data);
export const getLatentCap = () => client.get("/risk/latent-cap").then((r) => r.data);
export const getLlmSpend = (days) => client.get("/llm/spend", { params: days ? { days } : {} }).then((r) => r.data);
export const getPowerRecycle = (force) => client.get("/factory/power-recycle", { params: force ? { force: true } : {}, timeout: 120000 }).then((r) => r.data);
export const planPowerRecycle = (sig) => client.post(`/factory/power-recycle/plan/${sig}`, {}).then((r) => r.data);
export const setLlmSpendConfig = (cfg) => client.post("/llm/spend/config", cfg).then((r) => r.data);
export const getCopierProbe = () => client.get("/ctrader/copier-probe").then((r) => r.data);
export const runCopierProbe = (body) => client.post("/ctrader/copier-probe", body, { timeout: 120000 }).then((r) => r.data);
export const runLatentCap = () => client.post("/risk/latent-cap/run", {}, { timeout: 60000 }).then((r) => r.data);
export const setLatentCapConfig = (cfg) => client.post("/risk/latent-cap/config", cfg).then((r) => r.data);
export const setYoutubeScannerConfig = (cfg) => client.post("/factory/youtube/config", cfg).then((r) => r.data);
export const rebuildResearchPrior = () => client.post("/research/prior/rebuild", {}, { timeout: 60000 }).then((r) => r.data);

export const getFtmoAutopilot = () => client.get("/ftmo/autopilot").then((r) => r.data);

export const getFactoryFlow = () => client.get("/factory/flow").then((r) => r.data);
export const getFactoryFlowDetail = (id) => client.get("/factory/flow/detail", { params: { id } }).then((r) => r.data);
export const getIdeatorJourney = () => client.get("/factory/ideator/journey").then((r) => r.data);
export const getBrainVault = () => client.get("/factory/brain").then((r) => r.data);
export const getStarMap = () => client.get("/factory/starmap").then((r) => r.data);
export const getSniper = () => client.get("/polymarket/sniper").then((r) => r.data);
export const getSniperCalibration = () => client.get("/polymarket/sniper/calibration").then((r) => r.data);
export const setSniperSettings = (patch) => client.post("/polymarket/sniper/settings", patch).then((r) => r.data);
export const getCrowding = () => client.get("/factory/crowding").then((r) => r.data);

export default client;
